import { useCallback, useMemo } from 'react';
import { Button, Skeleton, Text } from '@mantine/core';
import { generatePath, Link } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.tsx';
import {
	PersistedViewKey,
	usePersistedCardTableView,
} from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView.ts';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { buildTableFiltersSearch } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { NO_MIP_FILTER_VALUE } from '@dealstack/shared/lib/documents/types';
import { usePreviewFile } from '@dealstack/frontend/shared/components/Documents/hooks/usePreviewFile';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { InvestorDetailsEmptyCardState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.tsx';
import { EMPTY_CARD_STATE_HEIGHT } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.styles.tsx';
import { useInvestorDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/useInvestorDocuments.ts';
import { getInvestorDocumentsTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocumentsTable.utils.tsx';
import { DocumentsCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/DocumentsCards.tsx';
import { DocumentsCardsSkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/DocumentsCardsSkeleton.tsx';
import { InvestorDocumentRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { CardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.types.ts';

interface Props {
	dealId: string;
	mipId: string;
	personIds: string[];
	viewStorageKey: PersistedViewKey;
	isInvestorLoading?: boolean;
	isInvestorError?: boolean;
}

export const DocumentsCard = ({
	dealId,
	mipId,
	personIds,
	viewStorageKey,
	isInvestorLoading = false,
	isInvestorError = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const dataOperations = useMantineTableDataOperations();
	const { previewFile, previewDocument, setPreviewDocument } = usePreviewFile();

	const {
		rows,
		isLoading: areDocumentsLoading,
		isError: haveDocumentsFailed,
	} = useInvestorDocuments({ dealId, mipId, personIds });

	const defaultView: CardTableView = rows.length > 2 ? 'table' : 'cards';

	const [view, setView, hasChosenView] = usePersistedCardTableView(viewStorageKey, defaultView);

	const isLoading = isInvestorLoading || areDocumentsLoading;
	const isError = isInvestorError || haveDocumentsFailed;

	const libraryPath = useMemo(() => {
		const path = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.DOCUMENT_LIBRARY}`, { dealId });
		const search = buildTableFiltersSearch([
			{ id: 'relatedToIds', value: personIds },
			{ id: 'mipIds', value: [mipId, NO_MIP_FILTER_VALUE] },
		]);

		return `${path}?${search}`;
	}, [dealId, personIds, mipId]);

	const onPreview = useCallback(
		(row: InvestorDocumentRow) =>
			previewFile(
				{
					documentId: row.id,
					fileId: row.fileId,
					name: row.name,
					fileExtension: row.fileExtension,
					classifiers: [],
					links: [],
					updatedAt: new Date().toISOString(),
				},
				dealId
			),
		[dealId]
	);

	const columns = useMemo(
		() => getInvestorDocumentsTableColumns({ tMipManage, onPreview }),
		[tMipManage, onPreview]
	);

	const renderContent = () => {
		if (isLoading) {
			// Without a stored choice the view depends on the row count, unknown until the rows load.
			if (view === 'cards' && hasChosenView) {
				return <DocumentsCardsSkeleton />;
			}

			return <Skeleton height={EMPTY_CARD_STATE_HEIGHT} radius="0.5rem" />;
		}

		if (isError) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-open-box"
					title={tMipManage('investors.details.documents.error_title')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENTS_ERROR}
				/>
			);
		}

		if (!rows.length) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-open-box"
					title={tMipManage('investors.details.documents.empty_title')}
					description={tMipManage('investors.details.documents.empty_description')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENTS_EMPTY}
				/>
			);
		}

		if (view === 'cards') {
			return <DocumentsCards rows={rows} onPreview={onPreview} />;
		}

		return (
			<MantineTable
				columns={columns}
				data={rows}
				dataOperations={dataOperations}
				manualFiltering={false}
				manualSorting={false}
				enablePagination={false}
				enableTopToolbar={false}
				dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENTS_TABLE}
			/>
		);
	};

	return (
		<div data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENTS_CARD}>
			<InvestmentDetailsCard
				cardLabelComponent={
					<Text fw={600} c="dark.8" className="label">
						{tMipManage('investors.details.documents.title')}
					</Text>
				}
				actions={
					<>
						{!!rows.length && (
							<Button
								component={Link}
								to={libraryPath}
								variant="default"
								size="md"
								color="dark.8"
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENTS_OPEN_LIBRARY
								}
							>
								{tMipManage('investors.details.documents.open_captable')}
							</Button>
						)}

						<CardTableViewSelector value={view} onViewChange={setView} />
					</>
				}
				variant="borderless"
			>
				{renderContent()}
			</InvestmentDetailsCard>

			{!!previewDocument && (
				<DocumentViewerModalV2
					opened={!!previewDocument}
					onClose={() => setPreviewDocument(undefined)}
					file={previewDocument.downloadUrl}
					fileExtension={previewDocument.fileExtension}
					fileName={previewDocument.fileName}
					isLoading={false}
					showDetailsByDefault={false}
				/>
			)}
		</div>
	);
};
